import * as tslib_1 from "tslib";
import { AfterViewInit, ChangeDetectorRef, ElementRef, EventEmitter, InjectionToken, OnDestroy, OnInit, } from '@angular/core';
import { TinyColor } from '@ctrl/tinycolor';
import { animationFrameScheduler, fromEvent, merge, Subject, } from 'rxjs';
import { debounceTime, filter, finalize, map, pairwise, sampleTime, share, takeUntil, tap, throttleTime, } from 'rxjs/operators';
import { AnimationObserver, FPS_DELAY, maxSideLength, MOUSE_THROTTLE, } from './ui-data-matrix.constants';
var PADDING_SIGMA = 4;
/**
 *  Mozilla does not preserve the previous event when using 'pairwise'
 *  Any required properties from the mouse event should be added in this map
 * @param ev mouse event source
 */
var partialMouseEvent = function (ev) {
    return ['offsetX', 'offsetY', 'clientX', 'clientY']
        .reduce(function (obj, property) {
        obj[property] = ev[property];
        return obj;
    }, {});
};
var ɵ0 = partialMouseEvent;
export var DATA_MATRIX_BACKGROUND_COLOR_RESOLVER = new InjectionToken('Data-Matrix-Background-Color');
var colorCache = {};
var UiDataMatrixComponent = /** @class */ (function () {
    function UiDataMatrixComponent(host, _cd, _backgroundColorResolver, _document) {
        var _this = this;
        this.host = host;
        this._cd = _cd;
        this._backgroundColorResolver = _backgroundColorResolver;
        this.border = 1;
        this.tooltipShowDelay = 300;
        this.animationDuration = 1000;
        this.cellHover = new EventEmitter();
        this.cellSelect = new EventEmitter();
        this.tooltipOptions = {
            x: 0,
            y: 0,
        };
        this.dimensions = {
            rows: 0,
            columns: 0,
            cell: {
                size: 0,
                count: 0,
            },
            width: 0,
            height: 0,
        };
        this._data = [];
        this._maxSide = 100;
        this._trigger = {
            draw: new Subject(),
            mouseMove: new Subject(),
        };
        this._lastMouseMoveEvent = null;
        this._destroyed$ = new Subject();
        this._animation = {
            complete: false,
            inProgress: false,
        };
        this._cellColor = function (_info) { return _info.isFocused ? 'rgba(255, 0, 0, 1)' : 'rgba(255, 0, 0, .75)'; };
        this._mapCell = function (ev) { return _this._cellInfo({
            column: Math.floor(ev.offsetX / _this.dimensions.cell.size),
            row: Math.floor(ev.offsetY / _this.dimensions.cell.size),
        }); };
        this._drawMatrix = function () {
            var _a;
            _a = maxSideLength(_this.host.nativeElement.clientWidth - PADDING_SIGMA, _this.host.nativeElement.clientHeight - PADDING_SIGMA, _this.cellCount, 0, _this._maxSide), _this.dimensions.cell.size = _a[0], _this.dimensions.columns = _a[1], _this.dimensions.rows = _a[2];
            _this.dimensions.width = _this.dimensions.columns * _this.dimensions.cell.size;
            _this.dimensions.height = _this.dimensions.rows * _this.dimensions.cell.size;
            _this._canvas.width = _this.dimensions.width;
            _this._canvas.height = _this.dimensions.height;
            _this._cd.detectChanges();
            if (_this.cellCount > 5000) {
                _this._animation.complete = true;
            }
            if (!_this._animation.complete &&
                !_this._animation.inProgress) {
                var frame$_1 = AnimationObserver({ size: 0, opacity: 0, progress: 0 }, { size: _this.dimensions.cell.size, opacity: 1, progress: 100 }, _this.animationDuration)
                    .pipe(share(), tap(function () { return _this._animation.inProgress = true; }), takeUntil(merge(_this._destroyed$, _this._trigger.draw)), finalize(function () {
                    _this._animation.complete = true;
                    _this._animation.inProgress = false;
                }));
                _this._traverse(function (info) { return _this._drawCellFrame(frame$_1, info); });
            }
            else {
                _this._traverse(_this._drawCell);
            }
            if (!_this._isMouseOverCanvas ||
                !_this._lastMouseMoveEvent) {
                return;
            }
            _this._trigger.mouseMove.next(_this._lastMouseMoveEvent);
        };
        this._drawTooltip = function (cell) {
            _this._tooltip.hidden = false;
            var tooltipX = cell.offset.x +
                _this.border -
                (_this._tooltip.clientWidth - _this.dimensions.cell.size) / 2;
            _this.tooltipOptions.x = Math.min(Math.max(tooltipX, 0), _this._canvas.width - _this._tooltip.clientWidth);
            _this.tooltipOptions.y = Math.max(_this.border, cell.offset.y - _this._tooltip.clientHeight - _this.border);
            _this._tooltip.style.top = _this.tooltipOptions.y + "px";
            _this._tooltip.style.left = _this.tooltipOptions.x + "px";
        };
        this._drawCell = function (info) {
            if (!_this._animation.complete ||
                info.index >= _this.cellCount) {
                return;
            }
            colorCache[_this._backgroundColor] = colorCache[_this._backgroundColor] || new TinyColor(_this._backgroundColor);
            var backgroundBase = colorCache[_this._backgroundColor].toRgb();
            var cellColor = _this._cellColor(info);
            var brightness = info.isFocused ? 9 : 0;
            var cacheKey = cellColor + "_" + brightness;
            if (!colorCache[cacheKey]) {
                var color = new TinyColor(_this._cellColor(info));
                var _a = color.toRgb(), r = _a.r, g = _a.g, b = _a.b, a = _a.a;
                colorCache[cacheKey] = new TinyColor({
                    r: (1 - a) * backgroundBase.r + a * r,
                    g: (1 - a) * backgroundBase.g + a * g,
                    b: (1 - a) * backgroundBase.b + a * b,
                }).brighten(info.isFocused ? 9 : 0);
            }
            _this._drawRect({
                x: info.offset.x,
                y: info.offset.y,
                offset: 0,
                width: _this.dimensions.cell.size,
                height: _this.dimensions.cell.size,
                border: _this.border,
                color: colorCache[cacheKey].toHexString(),
            });
        };
        this._drawCellFrame = function (frame$, info) {
            if (info.index >= _this.cellCount) {
                return;
            }
            frame$
                .subscribe(function (frame) {
                var cellColor = _this._cellColor(info);
                var cacheKey = cellColor + "_0";
                colorCache[cacheKey] = colorCache[cacheKey] || new TinyColor(_this._cellColor(info));
                var color = _this._getColor(frame, colorCache[cacheKey], info);
                _this._drawRect({
                    x: info.offset.x,
                    y: info.offset.y,
                    offset: 0,
                    width: _this.dimensions.cell.size,
                    height: _this.dimensions.cell.size,
                    border: _this.border,
                    color: color.toHexString(),
                });
            });
        };
        this._drawRect = function (_a) {
            var x = _a.x, y = _a.y, offset = _a.offset, width = _a.width, height = _a.height, border = _a.border, color = _a.color;
            _this._context.fillStyle = color;
            _this._context.fillRect(x + border + offset, y + border + offset, width - border, height - border);
        };
        this._cellInfo = function (coordinate) {
            var index = coordinate.column +
                coordinate.row * _this.dimensions.columns;
            return {
                position: coordinate,
                offset: {
                    x: coordinate.column * _this.dimensions.cell.size,
                    y: coordinate.row * _this.dimensions.cell.size,
                },
                index: index,
                data: _this.data[index],
                isFocused: false,
            };
        };
        this._backgroundColorResolver =
            this._backgroundColorResolver ||
                (function () { return 'white'; });
        this._window = _document['parentWindow'] ||
            _document.defaultView;
        merge(this._trigger.draw, fromEvent(this._window, 'resize')
            .pipe(debounceTime(FPS_DELAY)), fromEvent(this._window, 'transitionend')
            .pipe(map(function (ev) { return ev.srcElement || ev['originalTarget']; }), filter(function (source) {
            return (source.classList && source.classList.contains('mat-sidenav-content')) ||
                source === _this.host.nativeElement ||
                source === _this._canvas;
        }))).pipe(filter(function () { return _this._canvas != null; }), sampleTime(FPS_DELAY, animationFrameScheduler), takeUntil(this._destroyed$)).subscribe(this._drawMatrix);
    }
    Object.defineProperty(UiDataMatrixComponent.prototype, "cellCount", {
        get: function () {
            return this.dimensions.cell.count || this.data.length;
        },
        set: function (value) {
            if (value === this.dimensions.cell.count) {
                return;
            }
            this.dimensions.cell.count = value;
            this._trigger.draw.next();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UiDataMatrixComponent.prototype, "data", {
        get: function () {
            return this._data;
        },
        set: function (value) {
            if (!(value instanceof Array)) {
                return;
            }
            this._data = value || [];
            this._trigger.draw.next();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UiDataMatrixComponent.prototype, "maxSide", {
        set: function (value) {
            if (this._maxSide === value) {
                return;
            }
            this._maxSide = value;
            this._trigger.draw.next();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UiDataMatrixComponent.prototype, "cellColor", {
        set: function (value) {
            if (this._cellColor.toString() === value.toString()) {
                return;
            }
            this._cellColor = value;
            this._trigger.draw.next();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(UiDataMatrixComponent.prototype, "_backgroundColor", {
        get: function () {
            return this._backgroundColorResolver();
        },
        enumerable: true,
        configurable: true
    });
    UiDataMatrixComponent.prototype.ngOnInit = function () {
        var _this = this;
        this._canvas = this._canvasRef.nativeElement;
        this._tooltip = this._tooltipRef.nativeElement;
        this._context = this._canvas.getContext('2d');
        merge(fromEvent(this._canvas, 'mousemove'), this._trigger.mouseMove)
            .pipe(filter(function (ev) { return ev !== null; }), map(partialMouseEvent), throttleTime(MOUSE_THROTTLE), tap(function (ev) { return _this._lastMouseMoveEvent = ev; }), pairwise(), takeUntil(this._destroyed$))
            .pipe(map(function (_a) {
            var previous = _a[0], current = _a[1];
            return [
                _this._mapCell(previous),
                tslib_1.__assign({}, _this._mapCell(current), { isFocused: true })
            ];
        }), tap(function (_a) {
            var previous = _a[0], current = _a[1];
            if (previous.index !== current.index) {
                _this._tooltip.hidden = true;
                _this._drawCell(previous);
            }
            _this._drawCell(current);
        }))
            .pipe(tap(function (_a) {
            var _ = _a[0], current = _a[1];
            _this._canvas.style.cursor = _this.cellSelect.observers.length && current.index < _this.cellCount ?
                'pointer' :
                'default';
        }), tap(function (_a) {
            var _ = _a[0], current = _a[1];
            return current.index < _this.cellCount ? _this.cellHover.emit(current) : null;
        }), sampleTime(this.tooltipShowDelay, animationFrameScheduler), filter(function (_) { return !!_this._isMouseOverCanvas; }), filter(function (_) { return _this._tooltip.childNodes.length > 0; }), filter(function (_a) {
            var _ = _a[0], current = _a[1];
            return current.index < _this.cellCount;
        }))
            .subscribe(function (_a) {
            var _ = _a[0], current = _a[1];
            return _this._drawTooltip(current);
        });
        fromEvent(this._canvas, 'mouseleave')
            .pipe(takeUntil(this._destroyed$))
            .subscribe(function () {
            _this._tooltip.hidden = true;
            _this._isMouseOverCanvas = false;
            _this._drawMatrix();
        });
        fromEvent(this._canvas, 'mouseenter')
            .pipe(takeUntil(this._destroyed$))
            .subscribe(function () {
            _this._isMouseOverCanvas = true;
        });
        fromEvent(this._canvas, 'click')
            .pipe(map(partialMouseEvent), map(this._mapCell), filter(function (cell) { return cell.index < _this.cellCount; }), takeUntil(this._destroyed$))
            .subscribe(function (cell) { return _this.cellSelect.emit(tslib_1.__assign({}, cell, { isFocused: true })); });
    };
    UiDataMatrixComponent.prototype.ngAfterViewInit = function () {
        this._trigger.draw.next();
    };
    UiDataMatrixComponent.prototype.ngOnDestroy = function () {
        this._destroyed$.next();
    };
    UiDataMatrixComponent.prototype._traverse = function (predicate) {
        for (var row = 0; row < this.dimensions.rows; row++) {
            for (var column = 0; column < this.dimensions.columns; column++) {
                var info = this._cellInfo({
                    column: column,
                    row: row,
                });
                predicate(info);
            }
        }
    };
    UiDataMatrixComponent.prototype._getColor = function (frame, rgbColor, info) {
        colorCache[this._backgroundColor] = colorCache[this._backgroundColor] || new TinyColor(this._backgroundColor);
        var backgroundColor = colorCache[this._backgroundColor];
        // We are splitting the canvas length in 10 equal parts and we will calculate the cell color in the current frame based on
        // its position and the current animation progress (0-100).
        var progressDecile = frame.progress / 10;
        var columnsPerDecile = this.dimensions.columns / 10;
        // The gradient will cover 30% of the canvas at any frame
        var gradientPosition = {
            start: progressDecile * columnsPerDecile,
            end: (progressDecile + 3) * columnsPerDecile,
        };
        if (info.position.column > gradientPosition.end) {
            return backgroundColor;
        }
        // here we will compute the gradient
        var gradientLength = gradientPosition.end - gradientPosition.start;
        // as it is an horizontal gradient, we will determine the cell opacity based on its X-axis position within the gradient
        var columnXPosWithinGradient = info.position.column - gradientPosition.start;
        var halfRows = Math.floor(this.dimensions.rows / 2);
        // We need to create a mask over the gradient to create the 'stairs' effect:
        // - if we are in the upper half of the canvas, here are the cells we want to render:
        /**
         * 0 0 0 0 0 0 0 0 0 0
         * 1 1 0 0 0 0 0 0 0 0
         * 1 1 1 1 0 0 0 0 0 0
         * 1 1 1 1 1 1 0 0 0 0
         * 1 1 1 1 1 1 1 1 0 0
         * 1 1 1 1 1 1 1 1 1 1 <-- the middle row of the canvas
         * -------
         * 1 - the cells we want to display
         * 0 - the cells we want to hide
         */
        // So we need a gradient ratio between the gradient length and half the number of rows.
        // This will represent how many cells we want to hide based on the row number.
        var gradientRatio = gradientLength / halfRows;
        if (info.position.row < halfRows) {
            // upper half
            if (columnXPosWithinGradient > info.position.row * gradientRatio) {
                return backgroundColor;
            }
        }
        else {
            // lower half
            // similar logic as in the upper half,
            // only the way we determine the cells we want to hide based on the row number is different
            var hiddenCellsOnCurrentRow = (info.position.row - halfRows) * gradientRatio;
            if (columnXPosWithinGradient > gradientLength - hiddenCellsOnCurrentRow) {
                return backgroundColor;
            }
        }
        return rgbColor;
    };
    return UiDataMatrixComponent;
}());
export { UiDataMatrixComponent };
export { ɵ0 };
